import { useColorMode } from '../lib/color-mode.js';
import { useExtension } from '../lib/slots.js';
import { cn } from './ui.js';

/**
 * The deploy's mark: the client's icon when its web extension gives one, otherwise the
 * product name's first letter on the brand color. `className` sizes it; `tile` styles the
 * letter's tile only (its corners, shadow and type size), which an icon brings itself.
 */
export function BrandIcon({
  name,
  className,
  tile,
}: {
  name: string;
  className: string;
  tile: string;
}) {
  const brand = useExtension()?.brand;
  const { dark } = useColorMode();
  const icon = (dark && brand?.iconDark) || brand?.icon;
  // An icon made for a light page sits on a white plate on a dark one.
  const plate = dark && !brand?.iconDark && 'rounded-md bg-white p-0.5';
  if (icon)
    return <img src={icon} alt="" className={cn('shrink-0 object-contain', className, plate)} />;
  return (
    <div
      className={cn(
        'flex shrink-0 items-center justify-center bg-brand font-bold text-white',
        className,
        tile,
      )}
    >
      {name.slice(0, 1)}
    </div>
  );
}

/** The client's wide logo, which carries the product name itself; null when it gives none. On
 * a dark page, its dark version when it has one. */
export function useBrandLogo(): string | null {
  const brand = useExtension()?.brand;
  const { dark } = useColorMode();
  return (dark && brand?.logoDark) || brand?.logo || null;
}

/** The client's wide logo; one made only for light pages sits on a white plate on a dark one. */
export function BrandLogo({ alt, className }: { alt: string; className: string }) {
  const brand = useExtension()?.brand;
  const { dark } = useColorMode();
  const src = useBrandLogo();
  if (!src) return null;
  const plate = dark && !brand?.logoDark && 'rounded-md bg-white px-2 py-1';
  return <img src={src} alt={alt} className={cn(className, plate)} />;
}
